import React, { useContext, useState } from "react";
import { SubscriptionPaymentMethod } from "@shared/features/settings/subscription/types";
import { BuySubscriptionContext } from "features/settings/Subscription/BuySubscription.context";
import { getApiSlotsValidation } from "features/settings/common/apiSlotsValidation";
import {
  GetPaymentLinkRequest,
  LazyQueryStatus,
  MutationStatus,
  NewPaymentPriceResponse,
  PaymentLinkData,
} from "@shared/types";
import { sharedLib } from "@shared/services/shared.service";
import AntForm from "antd/es/form";
import { useUser } from "@shared/utils/hooks/misc.ts";
import { IS_WEB_VIEW } from "common/constants.ts";
import Text from "common/Typography/Text.tsx";
import moment from "moment";
import { dateToMoment, formatMoment } from "@shared/utils/date.js";
import { getUserExpiration } from "@shared/features/settings/subscription/utils.ts";
import { skipToken } from "@reduxjs/toolkit/query";
import PromoCode from "features/settings/Subscription/PromoCode.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import CryptoCheckout from "features/settings/Subscription/CryptoCheckout.tsx";
import PaymentMethodsCheckout from "features/settings/Subscription/PaymentMethodsCheckout.tsx";
import WebViewCheckout from "features/settings/Subscription/WebViewCheckout.tsx";
import PaypalCheckout from "features/settings/Subscription/PaypalCheckout.tsx";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay.tsx";

interface SummaryProps {
  getPaymentLink: () => void;
  setEmail: (email?: string) => void;
  email?: string;
  loadingPaymentLink?: boolean;
  onApplyButtonClick: () => void;
  baseRequest: GetPaymentLinkRequest | undefined;
  getPaymentLinkStatus: MutationStatus<PaymentLinkData, GetPaymentLinkRequest>;
  onRemovePromoClick: () => void;
  getNewPaymentPriceStatus?: LazyQueryStatus<NewPaymentPriceResponse>;
}

const Summary = ({
  getPaymentLink,
  setEmail,
  email,
  loadingPaymentLink,
  onApplyButtonClick,
  baseRequest,
  getPaymentLinkStatus,
  onRemovePromoClick,
  getNewPaymentPriceStatus,
}: SummaryProps) => {
  const {
    plan,
    duration,
    isUpgrade,
    isExtend,
    subGroupId,
    numApiSlots,
    subscriptionPaymentMethod,
    isAutoRenewal,
    isUpgradePricesLoading,
    isStandaloneApiSlots,
  } = useContext(BuySubscriptionContext)!;
  const slotsValidation = getApiSlotsValidation(numApiSlots ?? 0);
  const hasMinSlotsError = !!numApiSlots && slotsValidation.hasMinSlotsError;
  const isInvalidStep =
    !isExtend && !!numApiSlots && slotsValidation.isInvalidStep;
  const isPayDisabled = hasMinSlotsError || isInvalidStep;
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const [form] = AntForm.useForm();
  const { data: userGroupData } =
    sharedLib.sharedApi.common.useGetGroupDataQuery(
      subGroupId ? { sub_group_id: subGroupId } : skipToken
    );
  const { user } = useUser();
  const [acceptedCryptoTerms, setAcceptedCryptoTerms] = useState(false);

  const isCrypto =
    subscriptionPaymentMethod === SubscriptionPaymentMethod.CRYPTO;
  const isPaypal =
    subscriptionPaymentMethod === SubscriptionPaymentMethod.PAYPAL ||
    subscriptionPaymentMethod === SubscriptionPaymentMethod.CREDIT_CARD;
  const getGroupSubscriptionExpiration = () => {
    let groupExpiration = moment();
    if (userGroupData?.expiration_timestamp) {
      groupExpiration = moment.max(
        moment(),
        dateToMoment(userGroupData.expiration_timestamp, true)
      );
    }
    return formatMoment(groupExpiration.add(duration, "days"));
  };

  const getExpirationText = () => {
    const expiration = getUserExpiration(user, duration, isUpgrade);
    const autoRenewText = isAutoRenewal
      ? t("summary.auto_renew")
      : t("summary.expire");
    if (isUpgrade) {
      return (
        <Text>
          {t("summary.plan_upgraded", {
            plan: plan?.subscription_type,
            action: autoRenewText,
            date: expiration,
          })}
        </Text>
      );
    }
    const startAction =
      !subGroupId && user?.isExtendSubscription()
        ? t("summary.be_extended")
        : t("summary.start_today");
    return (
      <Text>
        {t("summary.plan_will_start", {
          startAction,
          action: autoRenewText,
          date: subGroupId ? getGroupSubscriptionExpiration() : expiration,
        })}
      </Text>
    );
  };

  const promoCodeRow = (
    <PromoCode
      onApplyButtonClick={onApplyButtonClick}
      onRemovePromoClick={onRemovePromoClick}
      getNewPaymentPriceStatus={getNewPaymentPriceStatus}
    />
  );

  const getCheckoutContent = () => {
    if (IS_WEB_VIEW) {
      return (
        <WebViewCheckout
          loadingPaymentLink={loadingPaymentLink}
          promoCodeRow={promoCodeRow}
          disabled={isPayDisabled}
        />
      );
    }
    if (isCrypto) {
      return (
        <CryptoCheckout
          form={form}
          setEmail={setEmail}
          email={email}
          getPaymentLink={getPaymentLink}
          expirationText={getExpirationText()}
          promoCodeRow={promoCodeRow}
          baseRequest={baseRequest}
          getPaymentLinkStatus={getPaymentLinkStatus}
          acceptedTerms={acceptedCryptoTerms}
          setAcceptedTerms={setAcceptedCryptoTerms}
        />
      );
    } else if (isPaypal) {
      return (
        <PaypalCheckout
          form={form}
          setEmail={setEmail}
          getPaymentLink={getPaymentLink}
          loadingPaymentLink={loadingPaymentLink}
          expirationText={getExpirationText()}
          promoCodeRow={promoCodeRow}
          disabled={isPayDisabled}
        />
      );
    }
  };

  return (
    <PaymentMethodsCheckout
      form={form}
      checkoutContent={
        <LoadingSpinnerOverlay
          isLoading={isUpgradePricesLoading && isStandaloneApiSlots}
          wrap={true}
        >
          {getCheckoutContent()}
        </LoadingSpinnerOverlay>
      }
    />
  );
};

export default Summary;
